import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import axios from "axios";
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Edit, Trash2, X, Info, Copy, Check, LoaderCircle, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};
const backend = isTouchDevice() ? TouchBackend : HTML5Backend;

// =================================================================
// TU LÓGICA ORIGINAL (SIN NINGÚN CAMBIO)
// =================================================================
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({ token });
    }
    setLoading(false);
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// =================================================================
// COMPONENTES VISUALES CON ESTILOS "TEAL" APLICADOS
// LA LÓGICA INTERNA DE CADA COMPONENTE SIGUE SIENDO LA TUYA
// =================================================================
const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🎵 Panel de Administración</h1>
          <p className="text-gray-600">Gestión de Géneros Musicales</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email de Administrador</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              placeholder="••••••••"
              required
            />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-teal-500 to-teal-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-teal-600 hover:to-teal-800 transition-all duration-200 disabled:opacity-50"
          >
            {loading ? 'Iniciando sesión...' : 'Acceder al Panel'}
          </button>
        </form>
        <div className="mt-6 text-center"><p className="text-sm text-gray-500">Panel exclusivo para administradores</p></div>
      </div>
    </div>
  );
};

const Modal = ({ isOpen, onClose, children, title }) => { // MODAL AHORA RECIBE TITLE
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800">{title || 'Modal'}</h3>
                    <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full"><X size={20} /></button>
                </div>
                <div className="overflow-y-auto">{children}</div>
            </div>
        </div>
    );
};

const EditGenreModal = ({ genre, isOpen, onClose, onSave }) => {
  const [name, setName] = useState(genre?.name || '');
  const [description, setDescription] = useState(genre?.description || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (genre) {
      setName(genre.name);
      setDescription(genre.description);
    }
  }, [genre]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSave(genre.id, { name, description });
    setLoading(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar Género">
      <form onSubmit={handleSave} className="p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Género</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descripción Técnica</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" rows="3" required />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button type="button" onClick={onClose} className="py-2 px-4 rounded-lg font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">Cancelar</button>
          <button type="submit" disabled={loading} className="py-2 px-4 rounded-lg font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 transition-colors">{loading ? 'Guardando...' : 'Guardar Cambios'}</button>
        </div>
      </form>
    </Modal>
  );
};

const DeleteConfirmModal = ({ genre, isOpen, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const handleDelete = async () => {
    setLoading(true);
    await onConfirm(genre.id);
    setLoading(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Confirmar Eliminación">
      <div className="p-6">
        <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100"><Trash2 className="h-6 w-6 text-red-600" /></div>
            <h3 className="text-lg font-semibold text-gray-900 mt-4">¿Eliminar Género?</h3>
            <p className="text-gray-600 mt-2">¿Estás seguro de que quieres eliminar "{genre?.name}"? Esta acción no se puede deshacer.</p>
        </div>
        <div className="flex justify-center gap-4 mt-6">
            <button onClick={onClose} className="w-full py-2 px-4 rounded-lg font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">Cancelar</button>
            <button onClick={handleDelete} disabled={loading} className="w-full py-2 px-4 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors">{loading ? 'Eliminando...' : 'Sí, eliminar'}</button>
        </div>
      </div>
    </Modal>
  );
};

const DraggableGenreCard = ({ genre, index, moveGenre, onEdit, onDelete }) => {
  const [{ isDragging }, drag] = useDrag({ type: 'GENRE', item: { id: genre.id, index }, collect: (m) => ({ isDragging: m.isDragging() }) });
  const [, drop] = useDrop({ accept: 'GENRE', hover: (item) => { if (item.index !== index) { moveGenre(item.index, index); item.index = index; } } });
  const opacity = isDragging ? 0.4 : 1;

  return (
    <div ref={(node) => drag(drop(node))} style={{ opacity }} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-lg hover:border-teal-400 transition-all duration-200 cursor-move select-none">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
                <span className="text-xl font-bold text-teal-600 select-none">{genre.emoji_number}</span>
                <h4 className="font-semibold text-gray-800 text-sm sm:text-base truncate">{genre.name}</h4>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{genre.description}</p>
        </div>
        <div className="flex flex-col space-y-1 ml-2">
            <button onClick={(e) => { e.stopPropagation(); onEdit(genre); }} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-full transition-colors" title="Editar"><Edit size={16} /></button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(genre); }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors" title="Eliminar"><Trash2 size={16} /></button>
        </div>
      </div>
    </div>
  );
};

const GenreManager = () => {
    // TU LÓGICA ORIGINAL ESTÁ AQUÍ, INTACTA
    const [genres, setGenres] = useState([]);
    const [apiKeys, setApiKeys] = useState([]);
    const [activeTab, setActiveTab] = useState('genres');
    const [loading, setLoading] = useState(false);
    const [newGenre, setNewGenre] = useState({ name: '', description: '' });
    const [newApiKeyName, setNewApiKeyName] = useState('');
    const [editingGenre, setEditingGenre] = useState(null);
    const [deletingGenre, setDeletingGenre] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isReordering, setIsReordering] = useState(false);
    const [hasReorderChanges, setHasReorderChanges] = useState(false);
    const { logout } = useAuth();
  
    useEffect(() => {
      loadGenres();
      loadApiKeys();
    }, []);
  
    const loadGenres = async () => {
      try {
        const response = await axios.get(`${API}/genres`);
        setGenres(response.data);
        setHasReorderChanges(false);
      } catch (error) {
        console.error('Error loading genres:', error);
      }
    };
  
    const loadApiKeys = async () => {
      try {
        const response = await axios.get(`${API}/api-keys`);
        setApiKeys(response.data);
      } catch (error) {
        console.error('Error loading API keys:', error);
      }
    };
  
    const moveGenre = (fromIndex, toIndex) => {
      const updatedGenres = [...genres];
      const [movedGenre] = updatedGenres.splice(fromIndex, 1);
      updatedGenres.splice(toIndex, 0, movedGenre);
      setGenres(updatedGenres);
      setHasReorderChanges(true);
    };
  
    const saveGenreOrder = async () => {
      if (!hasReorderChanges) return;
      setIsReordering(true);
      try {
        const genreIds = genres.map(genre => genre.id);
        await axios.put(`${API}/genres/reorder`, { genre_ids: genreIds });
        await loadGenres();
        setHasReorderChanges(false);
      } catch (error) {
        console.error('Error reordering genres:', error);
        alert('Error al reordenar géneros: ' + (error.response?.data?.detail || error.message));
        await loadGenres();
      }
      setIsReordering(false);
    };
  
    useEffect(() => {
      if (hasReorderChanges) {
        const timeoutId = setTimeout(() => { saveGenreOrder(); }, 1500);
        return () => clearTimeout(timeoutId);
      }
    }, [hasReorderChanges, genres]); // Agregado 'genres' para asegurar que el efecto se active con los cambios
  
    const initializeGenres = async () => {
      setLoading(true);
      try {
        await axios.post(`${API}/init-genres`);
        await loadGenres();
        alert('Géneros inicializados correctamente');
      } catch (error) {
        alert('Error al inicializar géneros: ' + (error.response?.data?.detail || error.message));
      }
      setLoading(false);
    };
  
    const createGenre = async (e) => {
      e.preventDefault();
      try {
        await axios.post(`${API}/genres`, newGenre);
        setNewGenre({ name: '', description: '' });
        await loadGenres();
      } catch (error) {
        alert('Error al crear género: ' + (error.response?.data?.detail || error.message));
      }
    };
  
    const updateGenre = async (id, data) => {
      try {
        await axios.put(`${API}/genres/${id}`, data);
        await loadGenres();
      } catch (error) {
        alert('Error al actualizar género: ' + (error.response?.data?.detail || error.message));
      }
    };
  
    const deleteGenre = async (id) => {
      try {
        await axios.delete(`${API}/genres/${id}`);
        await loadGenres();
      } catch (error) {
        alert('Error al eliminar género: ' + (error.response?.data?.detail || error.message));
      }
    };
  
    const createApiKey = async (e) => {
      e.preventDefault();
      try {
        await axios.post(`${API}/api-keys`, { name: newApiKeyName });
        setNewApiKeyName('');
        await loadApiKeys();
      } catch (error) {
        alert('Error al crear API key: ' + (error.response?.data?.detail || error.message));
      }
    };
  
    const deleteApiKey = async (id) => {
      if (window.confirm('¿Estás seguro de que quieres eliminar esta API key?')) {
        try {
          await axios.delete(`${API}/api-keys/${id}`);
          await loadApiKeys();
        } catch (error) {
          alert('Error al eliminar API key: ' + (error.response?.data?.detail || error.message));
        }
      }
    };
  
    const filteredGenres = genres.filter(genre =>
      genre.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      genre.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

  return (
    <DndProvider backend={backend}>
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm border-b sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-center py-4 space-y-2 sm:space-y-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">🎵 Panel de Gestión Musical</h1>
              <div className="flex items-center space-x-4">
                {(isReordering || hasReorderChanges) && (
                  <div className="flex items-center space-x-2 text-sm text-teal-600 font-medium">
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    <span>{isReordering ? 'Guardando orden...' : 'Cambios pendientes'}</span>
                  </div>
                )}
                <button onClick={logout} className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors text-sm sm:text-base font-semibold">
                  <LogOut size={16}/> Cerrar Sesión
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                <button onClick={() => setActiveTab('genres')} className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-semibold text-sm sm:text-base ${activeTab === 'genres' ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <span>Géneros</span> ({genres.length})
                </button>
                <button onClick={() => setActiveTab('apikeys')} className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-semibold text-sm sm:text-base ${activeTab === 'apikeys' ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <span>API Keys</span> ({apiKeys.length})
                </button>
              </nav>
            </div>

            {activeTab === 'genres' && (
              <div className="p-4 sm:p-6 space-y-6">
                {genres.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-4">No hay géneros configurados</p>
                    <button onClick={initializeGenres} disabled={loading} className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50">{loading ? 'Inicializando...' : 'Inicializar Géneros por Defecto'}</button>
                  </div>
                )}
                {genres.length > 0 && (
                  <div className="bg-teal-50 border-l-4 border-teal-400 rounded-r-lg p-4">
                    <div className="flex items-start space-x-3">
                        <Info className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <h4 className="font-semibold text-teal-900 text-sm sm:text-base">Cómo reordenar géneros</h4>
                            <p className="text-teal-800 text-xs sm:text-sm">Arrastra las tarjetas para cambiar el orden. Los cambios se guardan automáticamente.</p>
                        </div>
                    </div>
                  </div>
                )}
                <form onSubmit={createGenre} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm sm:text-base">Añadir Nuevo Género</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <input type="text" placeholder="Nombre del género" value={newGenre.name} onChange={(e) => setNewGenre({ ...newGenre, name: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm sm:text-base" required />
                    <input type="text" placeholder="Descripción técnica" value={newGenre.description} onChange={(e) => setNewGenre({ ...newGenre, description: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm sm:text-base" required />
                  </div>
                  <button type="submit" className="mt-4 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm sm:text-base font-semibold transition-colors">Añadir Género</button>
                </form>
                {genres.length > 0 && <input type="text" placeholder="Buscar géneros..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm sm:text-base" />}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {filteredGenres.map((genre, index) => <DraggableGenreCard key={genre.id} genre={genre} index={index} moveGenre={moveGenre} onEdit={setEditingGenre} onDelete={setDeletingGenre} />)}
                </div>
                {filteredGenres.length === 0 && searchTerm && <div className="text-center py-8"><p className="text-gray-600">No se encontraron géneros que coincidan con "{searchTerm}"</p></div>}
              </div>
            )}

            {activeTab === 'apikeys' && (
              <div className="p-4 sm:p-6 space-y-6">
                <form onSubmit={createApiKey} className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-4 text-sm sm:text-base">Crear Nueva API Key</h3>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <input type="text" placeholder="Nombre de la API Key (ej. App Móvil)" value={newApiKeyName} onChange={(e) => setNewApiKeyName(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm sm:text-base" required />
                        <button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm sm:text-base font-semibold transition-colors">Crear API Key</button>
                    </div>
                </form>
                <div className="space-y-4">
                    {apiKeys.map((apiKey) => {
                        const CopyButton = () => {
                            const [copied, setCopied] = useState(false);
                            const handleCopy = () => {
                                navigator.clipboard.writeText(apiKey.key).then(() => {
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 2000);
                                });
                            };
                            return (
                                <button onClick={handleCopy} className={`flex-shrink-0 w-28 text-white px-3 py-1 rounded text-xs sm:text-sm font-semibold transition-colors ${copied ? 'bg-green-500' : 'bg-teal-600 hover:bg-teal-700'}`}>
                                    {copied ? <div className="flex items-center justify-center gap-1"><Check size={14}/> Copiado</div> : <div className="flex items-center justify-center gap-1"><Copy size={14}/> Copiar Key</div>}
                                </button>
                            );
                        };
                        return (
                            <div key={apiKey.id} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{apiKey.name}</h4>
                                    <p className="text-xs text-gray-500 mt-1">Creada: {new Date(apiKey.created_at).toLocaleDateString()}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <p className="flex-1 text-sm text-gray-600 font-mono break-all bg-gray-100 p-2 rounded">{apiKey.key}</p>
                                        <CopyButton />
                                    </div>
                                </div>
                                <button onClick={() => deleteApiKey(apiKey.id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors self-start sm:self-center"><Trash2 size={16} /></button>
                            </div>
                        );
                    })}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </DndProvider>
  );
};

function App() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoaderCircle className="h-12 w-12 text-teal-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }
  return <div className="App">{user ? <GenreManager /> : <LoginForm />}</div>;
}

export default function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}